// cleanup_convert_to_typescript_file_PYMTCNTR
// cleanup_remove_external_libraries_PYMTCNTR

import { get, intersectionWith } from 'lodash';
import moment from 'moment';

import { datesToString } from '@glu/datepicker-react/lib/utils/formatString';

import forceServerTz from 'logic/utils/forceServerTimezone';
import formatAccount from 'utils/formatAccount';
import { numberToCurrency } from 'utils/currency';
import { API_DATE_FORMAT, DATE_DD_MMM_YYYY, DATE_FORMAT } from 'constants.js';

import { paymentsActions } from '../payments.columns';

export const dateFormat = /* istanbul ignore next */ (date) => moment(date).format(DATE_FORMAT);

export const getRowButtons =
  ({ actions, locale, permissions }) =>
  (model) => {
    const possibleActions = get(model.toJSON(), 'possibleActions', []);
    const editedPossibleActions = possibleActions.map((action) => action.toLowerCase());
    return intersectionWith(
      paymentsActions({ locale, actions, permissions }),
      editedPossibleActions,
      (all, possible) => all.label.toLowerCase() === possible
    );
  };

export const formatDefaultValues = ({ data, locale }) =>
  data.map((x) => {
    const keys = Object.keys(x);
    const mapped = keys.map((c) => {
      const isDefined = x[c] !== undefined && x[c] !== null && x[c] !== '';
      return {
        [c]: isDefined ? x[c] : locale['common.grid.empty']
      };
    });
    return mapped.reduce((acc, o) => ({ ...acc, ...o }), {});
  });

export const getPaymentConfirmationColumns = (columns, confirm, matchOrder) => {
  const confirmColumns = columns.filter((column) => {
    const elem = confirm.indexOf(column.field);
    if (elem !== -1) {
      return column;
    }
    return null;
  });
  if (matchOrder) {
    return confirmColumns.sort((a, b) => confirm.indexOf(a.field) - confirm.indexOf(b.field));
  }
  return confirmColumns;
};

export const aggregatePayments = ({ data, serverTimezone }) =>
  data.map((payment) => ({
    ...payment,
    amount: get(payment, 'amount'),
    paymentType: get(payment, 'paymentType'),
    fromAccountName: get(payment, 'fromAccountName'),
    status: get(payment, 'status'),
    companyId: get(payment, 'companyId'),
    payeeName: get(payment, 'payeeName'),
    companyName: get(payment, 'companyName'),
    companyEntryDescription: get(payment, 'companyEntryDescription'),
    accountType: get(payment, 'accountType'),
    achReceiver: get(payment, 'achReceiver'),
    activated: payment.activated && forceServerTz(moment(payment.activated), serverTimezone),
    addendum: get(payment, 'addendum'),
    approvalDate:
      payment.approvalDate && forceServerTz(moment(payment.approvalDate), serverTimezone),
    bankTrace: get(payment, 'bankTrace'),
    beneficiaryAccount: get(payment, 'beneficiaryAccount'),
    beneficiary: get(payment, 'beneficiary'),
    beneficiaryBank: get(payment, 'beneficiaryBank'),
    fromAccount: get(payment, 'fromAccount'),
    individualIdNumber: get(payment, 'individualIdNumber'),
    individualEntryState: get(payment, 'individualEntryState'),
    itemCount: get(payment, 'itemCount'),
    remainingApproval: get(payment, 'remainingApproval'),
    sameDay: get(payment, 'sameDay'),
    templateName: get(payment, 'templateName'),
    numCredits: get(payment, 'numCredits'),
    numDebits: get(payment, 'numDebits'),
    paymentCategory: get(payment, 'paymentCategory'),
    wireType: get(payment, 'wireType'),
    currency: get(payment, 'currency'),
    custTrace: get(payment, 'custTrace'),
    memo: get(payment, 'memo'),
    transactionDt:
      payment.transactionDt && forceServerTz(moment(payment.transactionDt), serverTimezone),
    wireEntryDate:
      payment.wireEntryDate && forceServerTz(moment(payment.wireEntryDate), serverTimezone)
  }));

export const formatInputDatepickerDisplay = (defaultPresets) => (startDate, endDate) => {
  const staticOption = Object.keys(defaultPresets).filter(
    (item) =>
      defaultPresets[item][0].isSame(startDate, 'seconds') &&
      defaultPresets[item][1].isSame(endDate, 'seconds')
  );
  return staticOption.length
    ? staticOption[0]
    : datesToString(startDate, endDate, {
        format: DATE_DD_MMM_YYYY,
        rangeDelimiter: ' - '
      });
};

export const setTxnDateStorage = ([startDate, endDate] = []) => {
  if (startDate && endDate)
    sessionStorage.setItem('paymentsTxnDate', JSON.stringify([startDate, endDate]));
};

export const getTxnDateStorage = () => JSON.parse(sessionStorage.getItem('paymentsTxnDate'));

export const setTxnDateFilter =
  (reload) =>
  (_name, [startDate, endDate]) => {
    setTxnDateStorage([startDate, endDate]);
    reload({ loading: true });
  };

export const getPaymentListPayload = (displayDatePicker) => {
  let payload = {};
  if (!displayDatePicker) {
    sessionStorage.removeItem('paymentsTxnDate');
  } else {
    const [startDate, endDate] = getTxnDateStorage() || [];
    if (startDate && endDate) {
      payload = {
        startDate: moment(startDate).format(API_DATE_FORMAT),
        endDate: moment(endDate).format(API_DATE_FORMAT)
      };
    }
  }
  return payload;
};

export const DEFAULT_PAYMENT_DATEPICKER_RANGE_PRESETS = (locale, tz = moment.tz.guess()) => ({
  [locale['payments.datePicker.options.today']]: [
    moment().tz(tz).startOf('day'),
    moment().tz(tz).endOf('day').add(1, 'year')
  ],
  [locale['payments.datePicker.options.yesterday']]: [
    moment().tz(tz).subtract(1, 'day').startOf('day'),
    moment().tz(tz).subtract(1, 'day').endOf('day')
  ],
  [locale['payments.datePicker.options.last7Days']]: [
    moment().tz(tz).subtract(7, 'day').startOf('day'),
    moment().tz(tz).endOf('day').add(1, 'year')
  ],
  [locale['payments.datePicker.options.last30Days']]: [
    moment().tz(tz).subtract(30, 'day').startOf('day'),
    moment().tz(tz).endOf('day')
  ],
  [locale['payments.datePicker.options.thisMonth']]: [
    moment().tz(tz).startOf('month').startOf('day'),
    moment().tz(tz).endOf('day')
  ],
  [locale['payments.datePicker.options.lastMonth']]: [
    moment().tz(tz).subtract(1, 'month').startOf('month').startOf('day'),
    moment().tz(tz).subtract(1, 'month').endOf('month').endOf('day')
  ]
});

export const getLoanAlertMessageDetails = ({ locale, payment }) => {
  const {
    amount,
    fromAccount,
    fromAccountName,
    fromAccountNickName,
    payeeName,
    payeeNickName,
    paymentType,
    toAccount
  } = payment || {};

  return [
    { head: locale['common.paymentType'], value: paymentType },
    {
      head: locale['common.fromAccount'],
      value: formatAccount({
        accountName: fromAccountName,
        accountNickName: fromAccountNickName,
        accountNumber: fromAccount
      })
    },
    {
      head: locale['common.toAccount'],
      value: formatAccount({
        accountName: payeeName,
        accountNickName: payeeNickName,
        accountNumber: toAccount
      })
    },
    { head: locale['common.amount'], value: numberToCurrency(amount) }
  ];
};
